import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Col, Container, Row, TabContent } from 'reactstrap';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import Loading from '../../../shared/components/custom/Loading';
import NavTab from '../../../shared/components/custom/NavTab';
import LazyTabPane from '../../../shared/components/custom/LazyTabPane';
import AlertModal, {
  AlertModalType,
} from '../../../shared/components/custom/AlertModal';
import QuestionCard from '../components/QuestionCard';
import { EventState } from '../context';
import SeriesForm from './components/SeriesForm';
import SeriesOccurrences from './components/SeriesOccurrences';
import SeriesParticipants from './components/SeriesParticipants';
import ApplyChangesModal from './components/ApplyChangesModal';
import SeriesUpdateResult from './components/SeriesUpdateResult';
import useEventFormOptions from './useEventFormOptions';
import { getApiErrorMessage, scheduleSummary } from './seriesUtils';
import {
  accessIsMissing,
  compact,
  detailsPayload,
  schedulePayload,
} from './seriesFormValues';

const SeriesPage = () => {
  const { seriesId } = useParams();
  const history = useHistory();
  const p = partial('EventSeries');
  const s = partial('shared');
  const o = partial('OfferForm');
  const options = useEventFormOptions();
  const [tab, setTab] = useState('1');
  const [series, setSeries] = useState(null);
  const [occurrences, setOccurrences] = useState([]);
  const [showPast, setShowPast] = useState(false);
  const [loading, setLoading] = useState(false);
  // A save waiting for the admin to pick which occurrences it applies to
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [updateResult, setUpdateResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const loadSeries = useCallback(async () => {
    try {
      setSeries(await Api.series.show(seriesId));
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  }, [seriesId]);

  // With past ones: from the series' first date; else from yesterday
  const pastFrom = showPast ? series?.schedule?.startDate : null;
  const loadOccurrences = useCallback(async () => {
    try {
      const data = await Api.series.occurrences(seriesId, {
        ...(pastFrom && {
          from: moment(pastFrom).startOf('day').toISOString(),
        }),
      });
      setOccurrences(data || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  }, [seriesId, pastFrom]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await loadSeries();
      setLoading(false);
    };
    load();
  }, [loadSeries]);

  useEffect(() => {
    loadOccurrences();
  }, [loadOccurrences]);

  const reload = async () => {
    await Promise.all([loadSeries(), loadOccurrences()]);
  };

  const onDetailsSubmit = (values) => {
    if (accessIsMissing(values)) {
      setShowAccessModal(true);
      return;
    }
    setPendingUpdate({
      payload: compact(detailsPayload(values, false)),
      image: values.imageUrl,
    });
  };

  const onScheduleSubmit = (values) => {
    setPendingUpdate({ payload: compact(schedulePayload(values)) });
  };

  const applyUpdate = async (applyTo) => {
    const { payload, image } = pendingUpdate;
    setSaving(true);
    setLoading(true);
    try {
      const result = await Api.series.update(seriesId, {
        ...payload,
        applyTo,
      });
      // Stored on the series and its future occurrences by the service
      if (image?.promise) {
        await image.promise('event', 'imageUrl', seriesId);
      }
      setPendingUpdate(null);
      setUpdateResult(result);
      toast.success(p('seriesSaved'));
      await reload();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const deleteSeries = async () => {
    setLoading(true);
    try {
      const { removed } = await Api.series.delete(seriesId);
      toast.success(p('seriesDeleted', { count: removed || 0 }));
      history.replace('/events');
    } catch (err) {
      setLoading(false);
      setShowDeleteModal(false);
      toast.error(getApiErrorMessage(err));
    }
  };

  const syncQuestions = async () => {
    try {
      const { queued } = await Api.series.syncQuestions(seriesId);
      toast.info(p('questionsSynced', { count: queued || 0 }));
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const now = moment();
  const upcoming = occurrences.filter(
    (occurrence) =>
      moment(occurrence.startDate).isAfter(now) && !occurrence.cancelledAt
  );
  const bookedUpcoming = upcoming.reduce(
    (total, occurrence) => total + (occurrence.bookedSeats || 0),
    0
  );
  const formProps = {
    series,
    eventCategories: options.eventCategories,
    eventLevels: options.eventLevels,
    eventGroups: options.eventGroups,
    eventTiers: options.eventTiers,
  };

  return (
    <Container className="series-page">
      <Loading loading={loading || !options.loaded} />
      <AlertModal
        visible={showAccessModal}
        modalType={AlertModalType.ERROR}
        handleModal={() => setShowAccessModal(false)}
        message={o('levelOrGroupRequiredMessage')}
      />
      <AlertModal
        visible={showDeleteModal}
        modalType={AlertModalType.DELETION}
        handleModal={() => setShowDeleteModal(false)}
        modalAction={deleteSeries}
        message={p('deleteConfirm')}
        subText={p('deleteConfirmSubtext')}
      />
      <ApplyChangesModal
        isOpen={!!pendingUpdate}
        occurrences={occurrences}
        busy={saving}
        onClose={() => setPendingUpdate(null)}
        onApply={applyUpdate}
      />
      <Link to="/events" className="series-page__back">
        <ArrowLeftIcon size={16} /> {p('backToEvents')}
      </Link>
      {series && (
        <>
          <div className="series-page__header">
            <div>
              <h3 className="page-title mb-1">
                {series.title}
                <Badge color="primary" pill className="ml-2 align-middle">
                  {p('series')}
                </Badge>
              </h3>
              <div className="text-muted">
                {[
                  scheduleSummary(series.schedule),
                  series.schedule?.durationMinutes &&
                    p('minutes', { count: series.schedule.durationMinutes }),
                  series.address,
                  s(series.targetAudience),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              <div className="series-page__stats">
                <span>
                  <strong>{upcoming.length}</strong> {p('upcoming')}
                </span>
                <span>
                  <strong>{bookedUpcoming}</strong> {p('bookedPlaces')}
                </span>
                <span>
                  <strong>{series.seriesBookings?.length || 0}</strong>{' '}
                  {p('wholeSeriesBookings')}
                </span>
              </div>
            </div>
            <Button
              outline
              color="danger"
              size="sm"
              className="mb-0"
              onClick={() => setShowDeleteModal(true)}
            >
              {p('deleteSeries')}
            </Button>
          </div>
          {updateResult && (
            <SeriesUpdateResult
              result={updateResult}
              occurrences={occurrences}
              onDismiss={() => setUpdateResult(null)}
            />
          )}
          <NavTab
            activeTab={tab}
            setTab={setTab}
            tabs={[
              p('tabOccurrences'),
              p('tabParticipants'),
              p('tabDetails'),
              p('tabSchedule'),
              p('tabQuestions'),
            ]}
          />
          <TabContent activeTab={tab}>
            <LazyTabPane activeTab={tab} tabId="1">
              <SeriesOccurrences
                series={series}
                occurrences={occurrences}
                showPast={showPast}
                onShowPastChange={setShowPast}
                keptIds={updateResult?.keptWithBookings || []}
                reload={loadOccurrences}
                setLoading={setLoading}
              />
            </LazyTabPane>
            <LazyTabPane activeTab={tab} tabId="2">
              <SeriesParticipants series={series} />
            </LazyTabPane>
            <LazyTabPane activeTab={tab} tabId="3">
              {options.loaded && (
                <Row>
                  <Col lg={9}>
                    <SeriesForm
                      {...formProps}
                      form="series_details_form"
                      sections={['details']}
                      onSubmit={onDetailsSubmit}
                    />
                  </Col>
                </Row>
              )}
            </LazyTabPane>
            <LazyTabPane activeTab={tab} tabId="4">
              {options.loaded && (
                <Row>
                  <Col lg={9}>
                    <SeriesForm
                      {...formProps}
                      form="series_schedule_form"
                      sections={['schedule']}
                      onSubmit={onScheduleSubmit}
                    />
                  </Col>
                </Row>
              )}
            </LazyTabPane>
            <LazyTabPane activeTab={tab} tabId="5">
              <p className="series-form__muted mb-3">
                {p('questionsTabHint')}{' '}
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 mb-0 align-baseline"
                  onClick={syncQuestions}
                >
                  {p('syncQuestions')}
                </button>
              </p>
              <EventState>
                <QuestionCard eventId={seriesId} onSaved={syncQuestions} />
              </EventState>
            </LazyTabPane>
          </TabContent>
        </>
      )}
    </Container>
  );
};

export default SeriesPage;
